import { DateTime, P } from '@piccolohealth/util';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  getMultiFactorResolver,
  type MultiFactorError,
  type MultiFactorResolver,
  type MultiFactorSession,
  multiFactor,
  onAuthStateChanged,
  onIdTokenChanged,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  signInWithEmailAndPassword,
  signOut,
  TotpMultiFactorGenerator,
  type TotpSecret,
  type User,
} from 'firebase/auth';
import React from 'react';
import { useIntercom } from 'react-use-intercom';
import { useConfig } from './ConfigContext';

export type StsTokenManager = {
  accessToken: string;
  expirationTime: number;
  isExpired: boolean;
};

export type AccessTokenResponse = {
  token: string;
  isExpired: boolean;
  expirationTime: number;
  currentTime: number;
};

export type AuthState =
  | { type: 'loading' }
  | { type: 'loggedOut' }
  | {
      type: 'loggedIn';
      user: User;
      isMfaEnrolled: boolean;
    };

export interface PhoneVerifyChallenge {
  type: 'phone';
  resolver: MultiFactorResolver;
  verificationId: string;
}

export interface PhoneEnrolChallenge {
  type: 'phone';
  phoneNumber: string;
  session: MultiFactorSession;
  verificationId: string;
}

export interface TotpVerifyChallenge {
  type: 'totp';
  resolver: MultiFactorResolver;
}

export interface TotpEnrolChallenge {
  type: 'totp';
  secret: TotpSecret;
  uri: string;
}

export type MultiFactorVerifyChallenge = PhoneVerifyChallenge | TotpVerifyChallenge;

export interface AuthContextProps {
  authState: AuthState;
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isRecentLogin: boolean;
  isEnrolledInMfa: boolean;
  showEnrolMfa: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  getMultiFactorVerifyChallenge: (error: MultiFactorError) => Promise<MultiFactorVerifyChallenge>;
  sendPhoneVerificationCode: (resolver: MultiFactorResolver) => Promise<PhoneVerifyChallenge>;
  verifyPhoneVerificationCode: (
    challenge: PhoneVerifyChallenge,
    verificationCode: string,
  ) => Promise<void>;
  sendPhoneEnrolmentCode: (phone: string) => Promise<PhoneEnrolChallenge>;
  verifyPhoneEnrolmentCode: (
    challenge: PhoneEnrolChallenge,
    verificationCode: string,
  ) => Promise<void>;
  verifyTotpVerificationCode: (
    challenge: TotpVerifyChallenge,
    verificationCode: string,
  ) => Promise<void>;
  generateTotpEnrolCode: () => Promise<TotpEnrolChallenge>;
  verifyTotpEnrolmentCode: (
    challenge: TotpEnrolChallenge,
    verificationCode: string,
  ) => Promise<void>;
  getAccessToken: (forceRefresh?: boolean) => Promise<AccessTokenResponse>;
  requestResetPasswordEmail: (email: string) => Promise<void>;
  resetPassword: (oobCode: string, email: string, password: string) => Promise<void>;
  requestVerifyInviteEmail: (email: string) => Promise<void>;
  verifyInvite: (oobCode: string, email: string, password: string) => Promise<void>;
  unenrolMfa: (oobCode: string) => Promise<void>;
}

const INVALID_SESSION_THRESHOLD = 4 * 60 * 1000; // 4 minutes

export const AuthContext = React.createContext<AuthContextProps | undefined>(undefined);

export const AuthContextProvider = (props: React.PropsWithChildren) => {
  const { config } = useConfig();
  const queryClient = useQueryClient();
  const { boot: bootIntercom, shutdown: shutdownIntercom } = useIntercom();

  const [isActiveLoginSession, setIsActiveLoginSession] = React.useState(false);
  const [authState, setAuthState] = React.useState<AuthState>({ type: 'loading' });

  const auth = React.useMemo(() => {
    const auth = getAuth(initializeApp(config.firebase));
    auth.settings.appVerificationDisabledForTesting = config.firebase.recaptchaDisabled;
    return auth;
  }, [config.firebase]);

  const isLoading = React.useMemo(() => {
    return authState.type === 'loading';
  }, [authState]);

  const user = React.useMemo(() => {
    if (authState.type !== 'loggedIn') {
      return null;
    }

    return authState.user;
  }, [authState]);

  const isAuthenticated = React.useMemo(() => {
    if (authState.type !== 'loggedIn') {
      return false;
    }

    return true;
  }, [authState]);

  const isRecentLogin = React.useMemo(() => {
    if (authState.type !== 'loggedIn') {
      return false;
    }

    const lastSignInTime = authState.user?.metadata.lastSignInTime;

    if (!lastSignInTime) {
      return false;
    }

    return (
      DateTime.fromRFC2822(lastSignInTime).diffNow('milliseconds').milliseconds >
      INVALID_SESSION_THRESHOLD
    );
  }, [authState]);

  const isEnrolledInMfa = React.useMemo(() => {
    return authState.type === 'loggedIn' && authState.isMfaEnrolled;
  }, [authState]);

  const showEnrolMfa = React.useMemo(() => {
    return authState.type === 'loggedIn' && !authState.isMfaEnrolled && isActiveLoginSession;
  }, [authState, isActiveLoginSession]);

  React.useEffect(() => {
    const onUserChanged = async (user: User | null) => {
      if (user) {
        const isMfaEnrolled = multiFactor(user).enrolledFactors.length > 0;

        setAuthState({
          type: 'loggedIn',
          user,
          isMfaEnrolled,
        });
      } else {
        setAuthState({ type: 'loggedOut' });
      }
    };

    const onAuthStateChangedUnsubscribe = onAuthStateChanged(auth, (user) => {
      onUserChanged(user).catch(console.error);
    });

    const idTokenChangedUnsubscribe = onIdTokenChanged(auth, (user) => {
      onUserChanged(user).catch(console.error);
    });

    return () => {
      onAuthStateChangedUnsubscribe();
      idTokenChangedUnsubscribe();
    };
  }, [auth]);

  const login = React.useCallback(
    async (email: string, password: string): Promise<User> => {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      setIsActiveLoginSession(true);
      return user;
    },
    [auth],
  );

  const logout = React.useCallback(async (): Promise<void> => {
    await signOut(auth);
    // clear user
    setAuthState({ type: 'loggedOut' });
    // clear react-query cache so that the user data is not cached
    queryClient.clear();
    shutdownIntercom();
    bootIntercom();
  }, [auth, queryClient, shutdownIntercom, bootIntercom]);

  const getAccessToken = React.useCallback(
    async (forceRefresh?: boolean): Promise<AccessTokenResponse> => {
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error('User is not authenticated');
      }

      const token = await currentUser.getIdToken(forceRefresh);
      const tokenManager: StsTokenManager = (currentUser as any).stsTokenManager;

      return {
        token,
        currentTime: Date.now(), // same as what token manager does internally
        expirationTime: tokenManager.expirationTime,
        isExpired: tokenManager.isExpired,
      };
    },
    [auth],
  );

  const sendPhoneVerificationCode = React.useCallback(
    async (resolver: MultiFactorResolver): Promise<PhoneVerifyChallenge> => {
      const recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha', { size: 'invisible' });
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const phoneOptions = {
        multiFactorHint: resolver.hints[0],
        session: resolver.session,
      };

      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneOptions,
        recaptchaVerifier,
      );

      // Clear the recaptcha verifier after the phone number is verified. Must recreate the recaptcha div
      recaptchaVerifier.clear();
      document.getElementById('recaptcha-container')!.innerHTML = '<div id="recaptcha"></div>';

      return { type: 'phone', resolver, verificationId };
    },
    [auth],
  );

  const verifyPhoneVerificationCode = React.useCallback(
    async (challenge: PhoneVerifyChallenge, verificationCode: string): Promise<void> => {
      const credential = PhoneAuthProvider.credential(challenge.verificationId, verificationCode);
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);

      return challenge.resolver.resolveSignIn(multiFactorAssertion).then(P.noop);
    },
    [],
  );

  const sendPhoneEnrolmentCode = React.useCallback(
    async (phoneNumber: string): Promise<PhoneEnrolChallenge> => {
      if (authState.type !== 'loggedIn') {
        throw new Error('User is not logged in');
      }

      const session = await multiFactor(authState.user).getSession();
      const recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha', { size: 'invisible' });
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const phoneOptions = {
        phoneNumber,
        session,
      };

      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneOptions,
        recaptchaVerifier,
      );

      // Clear the recaptcha verifier after the phone number is verified. Must recreate the recaptcha div
      recaptchaVerifier.clear();
      document.getElementById('recaptcha-container')!.innerHTML = '<div id="recaptcha"></div>';

      return { type: 'phone', phoneNumber, verificationId, session };
    },
    [auth, authState],
  );

  const verifyPhoneEnrolmentCode = React.useCallback(
    async (challenge: PhoneEnrolChallenge, verificationCode: string): Promise<void> => {
      if (authState.type !== 'loggedIn') {
        return logout();
      }

      const { verificationId } = challenge;

      const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

      await multiFactor(authState.user).enroll(multiFactorAssertion, 'Phone number');
      await getAccessToken(true);
    },
    [authState, getAccessToken, logout],
  );

  const generateTotpEnrolCode = React.useCallback(async (): Promise<TotpEnrolChallenge> => {
    if (authState.type !== 'loggedIn' || !authState.user.email) {
      throw new Error('User is not logged in');
    }

    const multiFactorSession = await multiFactor(authState.user).getSession();
    const secret = await TotpMultiFactorGenerator.generateSecret(multiFactorSession);
    const uri = secret.generateQrCodeUrl(authState.user.email, 'Piccolo Health');
    return { type: 'totp', uri, secret };
  }, [authState]);

  const getMultiFactorVerifyChallenge = React.useCallback(
    async (error: MultiFactorError): Promise<MultiFactorVerifyChallenge> => {
      const resolver = getMultiFactorResolver(auth, error);
      const factorId = resolver.hints[0]?.factorId;
      const type = factorId === 'totp' ? 'totp' : 'phone';

      switch (type) {
        case 'phone': {
          const challenge = await sendPhoneVerificationCode(resolver);
          return { ...challenge };
        }
        case 'totp': {
          return { type, resolver };
        }
      }
    },
    [auth, sendPhoneVerificationCode],
  );

  const verifyTotpEnrolmentCode = React.useCallback(
    async (challenge: TotpEnrolChallenge, verificationCode: string): Promise<void> => {
      if (authState.type !== 'loggedIn') {
        return logout();
      }

      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
        challenge.secret,
        verificationCode,
      );
      await multiFactor(authState.user).enroll(multiFactorAssertion, 'TOTP');
      await getAccessToken(true);
    },
    [logout, authState, getAccessToken],
  );

  const verifyTotpVerificationCode = React.useCallback(
    async (challenge: TotpVerifyChallenge, verificationCode: string): Promise<void> => {
      const challengeResolverHint = challenge.resolver.hints[0];

      if (!challengeResolverHint) {
        throw new Error('Unable to find challenge resolver uid');
      }

      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
        challengeResolverHint.uid,
        verificationCode,
      );

      return await challenge.resolver.resolveSignIn(multiFactorAssertion).then(P.noop);
    },
    [],
  );

  const requestResetPasswordEmail = async (email: string): Promise<void> => {
    await axios.post(`${config.api.webUrl}/users/request-reset-password-email`, { email });
  };

  const resetPassword = async (oobCode: string, email: string, password: string): Promise<void> => {
    await axios.post(`${config.api.webUrl}/users/reset-password`, { oobCode, email, password });
  };

  const requestVerifyInviteEmail = async (email: string): Promise<void> => {
    await axios.post(`${config.api.webUrl}/users/request-verify-invite-email`, { email });
  };

  const verifyInvite = async (oobCode: string, email: string, password: string): Promise<void> => {
    await axios.post(`${config.api.webUrl}/users/verify-invite`, { oobCode, email, password });
  };

  const unenrolMfa = async (oobCode: string): Promise<void> => {
    await axios.post(`${config.api.webUrl}/users/unenrol-mfa`, { oobCode });
  };

  return (
    <AuthContext.Provider
      value={{
        authState,
        user,
        isLoading,
        isAuthenticated,
        isRecentLogin,
        isEnrolledInMfa,
        showEnrolMfa,
        login,
        logout,
        getAccessToken,
        getMultiFactorVerifyChallenge,
        sendPhoneVerificationCode,
        verifyPhoneVerificationCode,
        sendPhoneEnrolmentCode,
        verifyPhoneEnrolmentCode,
        verifyTotpVerificationCode,
        generateTotpEnrolCode,
        verifyTotpEnrolmentCode,
        requestResetPasswordEmail,
        resetPassword,
        requestVerifyInviteEmail,
        verifyInvite,
        unenrolMfa,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
